import { CheckBox } from "@shltelemedicine/shl-ui-lib";
import { useCallback, useEffect, useRef } from "react";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { FormattedMessage } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import { Constants } from "../../../../constants";
import {
  EcgDetailsActionType,
  useEcgDetailsContext,
} from "../../../../context/EcgDetailsContext";
import { IDynamicData } from "../../../../models/dynamic-data.interface";
import { arrayToChunks } from "../../../../utils/sharedUtil";

const numColumnsInFindings = 3;

type FindingsProps = {
  showLeadsPane: (findingKey: string, findingName: string) => void;
};

const Findings = ({ showLeadsPane }: FindingsProps) => {
  const {
    state: { loading, ecgEdit, draft, ecgEditSnapshot },
    actions: { edit },
    dispatch,
  } = useEcgDetailsContext();

  const isMounted = useRef(false);

  const setEcgSnapShot = useCallback(() => {
    //  Save latest snapshot of ECG edit (Without any "Unreadable" related info),
    const ecgSnapshot = {
      ...ecgEdit,
      isReadable: true,
      findings:
        ecgEdit?.findings?.map((f) => {
          return {
            ...f,
            value:
              f.key === Constants.ECG_UNREADABLE_FINDINGS_CODE_1
                ? false
                : f.value,
          };
        }) ?? [],
    };
    dispatch({
      type: EcgDetailsActionType.SET_ECG_EDIT_SNAPSHOT,
      payload: JSON.stringify(ecgSnapshot),
    });
  }, [dispatch, ecgEdit]);

  const handleUnreadableChange = useCallback(() => {
    const isReadable = ecgEdit?.isReadable;

    if (isReadable === null || isReadable === undefined) {
      return;
    }

    if (isReadable) {
      // If isReadable - Reset edit to latest snapshot
      if (!!ecgEditSnapshot) {
        edit(JSON.parse(ecgEditSnapshot));
      }
      return;
    }

    /**
     * If we get here, Ecg is Unreadable.
     * Save latest snapshot of ECG edit,
     * and clear editable fields
     */
    setEcgSnapShot();

    // Clear findings
    const findings = ecgEdit?.findings ?? [];
    for (let i = 0; i < findings.length; i++) {
      const finding = findings![i];

      // Select unreadable findings code 99
      if (finding.key === Constants.ECG_UNREADABLE_FINDINGS_CODE_1) {
        finding.value = true;
      }
      // Uncheck all other findings (non isReadable related)
      else {
        finding.value = false;
      }
    }

    // Clear actions
    if (ecgEdit?.actions) {
      for (let i = 0; i < ecgEdit.actions.length; i++) {
        const action = ecgEdit.actions[i];
        action.value = false;
      }
    }

    // Clear interpretation
    if (ecgEdit?.interpretation) {
      ecgEdit.interpretation = undefined;
    }

    edit(ecgEdit!);

    if (draft.show) {
      dispatch({
        type: EcgDetailsActionType.SET_DRAFT,
        payload: {
          ...draft,
          show: false,
        },
      });
    }
  }, [ecgEdit, ecgEditSnapshot, edit, setEcgSnapShot, draft, dispatch]);

  useEffect(() => {
    if (isMounted.current) {
      handleUnreadableChange();
    } else {
      isMounted.current = true;
    }
  }, [ecgEdit?.isReadable]); // eslint-disable-line

  const onToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!ecgEdit?.findings || !ecgEdit.findings.length) return;

    const id = event.currentTarget.name;
    const finding = ecgEdit.findings.find((f) => f.key === id);

    if (!finding) return;

    finding.value = !finding.value;

    // If toggled finding use leads
    if (finding.useLeads) {
      if (finding.value) {
        // If finding was selected, show finding's Leads
        showLeadsPane(finding.key, finding.displayName);
      } else if (finding?.leads?.length) {
        // Uncheck the finding leads (Since the finding was unchecked)
        for (let i = 0; i < finding.leads.length; i++) {
          const lead = finding.leads[i];
          lead.value = false;
        }
      }
    }

    if (finding.key === Constants.ECG_UNREADABLE_FINDINGS_CODE_1) {
      // Finding 99 selected -> isReadable = false
      ecgEdit.isReadable = !finding.value;
    }

    edit(ecgEdit);
  };

  return (
    <Form className="p-2">
      <p className="mb-1 font-sm">
        <span className="prim-color">*</span>&nbsp;
        <FormattedMessage
          id="ECG_DETAILS.SELECT_FINDINGS"
          defaultMessage="Check to select finding from list"
        />
      </p>

      <Form.Row>
        {ecgEdit?.findings &&
          arrayToChunks(ecgEdit.findings, numColumnsInFindings).map(
            (findingsChunck) => (
              <Col
                xs={12}
                md={12 / numColumnsInFindings}
                className="align-items-center font-md my-md-3"
                key={uuidv4()}
              >
                {findingsChunck.map((finding: IDynamicData) => (
                  <Form.Row
                    key={uuidv4()}
                    className="mb-md-2 mb-3 px-md-0 px-3"
                  >
                    <CheckBox
                      name={finding.key}
                      checked={finding.value}
                      label={finding.displayName}
                      onChange={onToggle}
                      disabled={loading || ecgEdit?.isReadable === false}
                    />
                  </Form.Row>
                ))}
              </Col>
            )
          )}
      </Form.Row>
    </Form>
  );
};

export default Findings;
